// biome-ignore-all lint/suspicious/noAssignInExpressions: TODO - address later
// ----------------------------------------------------------------------------
// NOTE: This module intentionally contains **zero** React/DOM code

// ---------------------------------------------------------------------------
// Public Types
// ---------------------------------------------------------------------------

// All operators currently understood by the server (mirrors python enum)
export type FilterOperator =
  | "IN"
  | "NOT_IN"
  | "EQ"
  | "NEQ"
  | "GT"
  | "GTE"
  | "LT"
  | "LTE"
  | "BETWEEN"
  | "LIKE"
  | "ILIKE"
  | "STARTS_WITH"
  | "ENDS_WITH"
  | "IS_NULL"
  | "IS_NOT_NULL";

// Atomic predicate (leaf node)
export interface StructuredFilter {
  field: string | null; // Column / property id
  op: FilterOperator | null;
  /**
   * We keep the values array optional so operators like IS_NULL have a more
   * ergonomic shape, but callers should still pass an empty array instead of
   * omitting the key entirely if they need strict contract equality.
   * Now also allowing null.
   */
  values?: unknown[] | null;
}

// Recursive AND/OR expression organised as a tree
export interface FilterGroup {
  operator: "AND" | "OR";
  filters: Array<StructuredFilter | FilterGroup>;
}

// Type definition for metric filters
export interface MetricsFilters {
  account_filters?: FilterGroup;
  opportunity_filters?: FilterGroup;
  touchpoint_filters?: FilterGroup;
  // future filters can be added here
}

// ---------------------------------------------------------------------------
// Operator helpers – UI helpers only (authoritative list comes from server)
// ---------------------------------------------------------------------------

const DEFAULT_OPS: FilterOperator[] = ["EQ", "NEQ", "IS_NULL", "IS_NOT_NULL"];

const OPS_BY_TYPE: Record<string, FilterOperator[]> = {
  TEXT: ["IN", "NOT_IN", "LIKE", "ILIKE", ...DEFAULT_OPS],
  NUMBER: ["IN", "NOT_IN", "GT", "GTE", "LT", "LTE", ...DEFAULT_OPS],
  BOOLEAN: DEFAULT_OPS,
  DATE: ["BETWEEN", "GT", "GTE", "LT", "LTE", ...DEFAULT_OPS],
  DATETIME: ["BETWEEN", "GT", "GTE", "LT", "LTE", ...DEFAULT_OPS],
  ARRAY: ["IN", "NOT_IN", ...DEFAULT_OPS],
};

/**
 * Returns the list of operators the UI should offer for a given data type.
 * Falls back to a conservative default (EQ/NEQ/NULL ops) when encountering
 * an unknown type so we never completely block the user.
 */
export function getValidOperators(dataType: string): FilterOperator[] {
  return OPS_BY_TYPE[dataType] || DEFAULT_OPS;
}

// ---------------------------------------------------------------------------
// Lightweight helpers
// ---------------------------------------------------------------------------

export function createFilter(
  field: string,
  op: FilterOperator,
  values: unknown[] = [],
): StructuredFilter {
  return { field, op, values };
}

export function isFilterGroup(node: unknown): node is FilterGroup {
  return (
    typeof node === "object" &&
    node !== null &&
    "operator" in node &&
    "filters" in node
  );
}

/**
 * Simple debugger utility that prints a FilterGroup as human-readable text –
 * useful for storybook/tooltips. This is **not** meant for production logging.
 */
export function prettyPrintFilterGroup(group: FilterGroup, indent = 0): string {
  const pad = " ".repeat(indent);
  const parts: string[] = [];

  group.filters.forEach((f) => {
    if (isFilterGroup(f)) {
      parts.push(`${pad}(${prettyPrintFilterGroup(f, indent + 2)})`);
    } else {
      const field = f.field ?? "<unknown field>";
      const op = f.op ?? "<unknown op>";
      parts.push(
        `${pad}${field} ${op} ${
          Array.isArray(f.values) ? f.values.join(", ") : ""
        }`,
      );
    }
  });

  return parts.join(` ${group.operator} `);
}

// ---------------------------------------------------------------------------
// Generic builder to convert legacy column filter state → FilterGroup
// ---------------------------------------------------------------------------

/**
 * Converts the flat map shape returned by the list-filter hooks (columnKey → UI value)
 * into a `FilterGroup` understood by the server.
 * The algorithm mirrors the earlier bespoke logic that lived in the two hooks but
 * is now centralised to avoid duplication.
 *
 * Supported value shapes:
 *   • undefined / null / ""          ⇒ ignored
 *   • string[]                        ⇒ IN
 *   • { values?, nullFilter? }        ⇒ IN + IS_NULL/IS_NOT_NULL for select filters
 *   • { notInValues? }                ⇒ NOT_IN for negated select filters
 *   • { min?, max?, nullFilter? }     ⇒ GTE/LTE + IS_NULL/IS_NOT_NULL for numeric filters
 *   • { from?, to?, nullFilter? }     ⇒ GTE/LTE + IS_NULL/IS_NOT_NULL for date filters
 *   • { value?, nullFilter? }         ⇒ ILIKE + IS_NULL/IS_NOT_NULL for text filters
 *   • { neqValues? }                  ⇒ NEQ for explicit not-equals values
 *   • primitive (string | number)     ⇒ ILIKE text match
 *
 * @param filters  Raw filter map from hook
 * @param validKeys  Column keys that are still present in the table
 */
export function buildFilterGroupFromColumnState(
  filters: Record<string, unknown>,
  validKeys?: Set<string>,
): FilterGroup | undefined {
  const structured: StructuredFilter[] = [];

  Object.entries(filters).forEach(([key, value]) => {
    if (key === "sortColumn" || key === "sortDirection" || key === "search") {
      return;
    }
    if (validKeys && !validKeys.has(key)) return;

    if (value === null || value === undefined || value === "") return;

    if (Array.isArray(value)) {
      if (value.length === 0) return;

      structured.push({ field: key, op: "IN", values: value });
      return;
    }

    if (typeof value === "object" && value !== null) {
      const v = value as Record<string, unknown>;

      // Handle null filter first
      if (v.nullFilter === "is_null") {
        structured.push({ field: key, op: "IS_NULL", values: [] });
        return; // When is_null is set, ignore other filter values
      }
      if (v.nullFilter === "is_not_null") {
        structured.push({ field: key, op: "IS_NOT_NULL", values: [] });
        // Continue processing other filter values when is_not_null is set
      }

      // Handle select filter values
      if (Array.isArray(v.values) && v.values.length > 0) {
        structured.push({ field: key, op: "IN", values: v.values });
      }

      // Handle negated select filter values (NOT_IN)
      if (Array.isArray((v as Record<string, unknown>).notInValues)) {
        const notInValues = (v as Record<string, unknown>)
          .notInValues as unknown[];
        if (notInValues.length > 0) {
          structured.push({ field: key, op: "NOT_IN", values: notInValues });
        }
      }

      // Handle range filters (number)
      const hasMin = v.min !== undefined && v.min !== "";
      const hasMax = v.max !== undefined && v.max !== "";

      // Handle date range filters
      const hasFrom = v.from !== undefined && v.from !== "";
      const hasTo = v.to !== undefined && v.to !== "";

      // Handle text filter
      const hasValue = v.value !== undefined && v.value !== "";

      if (hasMin) structured.push({ field: key, op: "GTE", values: [v.min!] });
      if (hasMax) structured.push({ field: key, op: "LTE", values: [v.max!] });
      // For date filters, we use an inclusive start and inclusive end
      if (hasFrom)
        structured.push({ field: key, op: "GTE", values: [v.from!] });
      if (hasTo) structured.push({ field: key, op: "LTE", values: [v.to!] });
      // For text filters
      if (hasValue)
        structured.push({ field: key, op: "ILIKE", values: [v.value!] });

      // Handle explicit not-equals values (NEQ)
      if (Array.isArray((v as Record<string, unknown>).neqValues)) {
        const neqValues = (v as Record<string, unknown>).neqValues as unknown[];
        if (neqValues.length > 0) {
          structured.push({ field: key, op: "NEQ", values: neqValues });
        }
      }

      return;
    }

    // Fallback text comparison
    structured.push({ field: key, op: "ILIKE", values: [String(value)] });
  });

  if (structured.length === 0) return undefined;

  return { operator: "AND", filters: structured };
}

export function convertToClientFilters(
  filterGroup: unknown,
): Record<string, unknown> {
  if (
    !filterGroup ||
    typeof filterGroup !== "object" ||
    !("filters" in filterGroup)
  ) {
    return {};
  }

  // We build a per-field accumulator and merge multiple nodes for the same field
  const clientFilters: Record<string, Record<string, unknown>> = {};
  const nodes = (filterGroup as { filters: unknown[] }).filters;

  if (Array.isArray(nodes)) {
    nodes.forEach((node) => {
      if (!node || typeof node !== "object") return;
      const candidate = node as {
        field?: unknown;
        op?: unknown;
        values?: unknown[] | null;
      };
      if (typeof candidate.field !== "string") return;

      const fieldKey = candidate.field;
      const op: string | undefined =
        typeof candidate.op === "string" ? candidate.op : undefined;
      const values = Array.isArray(candidate.values)
        ? candidate.values
        : candidate.values == null
          ? []
          : [candidate.values];

      const target = (clientFilters[fieldKey] ||= {});

      switch (op) {
        case "IS_NULL": {
          target.nullFilter = "is_null";
          break;
        }
        case "IS_NOT_NULL": {
          target.nullFilter = "is_not_null";
          break;
        }
        case "ILIKE":
        case "LIKE": {
          if (values.length > 0) target.value = values[0];
          break;
        }
        case "GT":
        case "GTE": {
          if (values.length > 0) {
            target.min = values[0];
            target.from = values[0];
          }
          break;
        }
        case "LT":
        case "LTE": {
          if (values.length > 0) {
            target.max = values[0];
            target.to = values[0];
          }
          break;
        }
        case "BETWEEN": {
          if (values.length >= 2) {
            target.min = values[0];
            target.from = values[0];
            target.max = values[1];
            target.to = values[1];
          }
          break;
        }
        case "IN": {
          const current: unknown[] = Array.isArray(target.values)
            ? (target.values as unknown[])
            : [];
          target.values = current.concat(values);
          break;
        }
        case "NOT_IN": {
          const current: unknown[] = Array.isArray(
            (target as Record<string, unknown>).notInValues,
          )
            ? ((target as Record<string, unknown>).notInValues as unknown[])
            : [];
          (target as Record<string, unknown>).notInValues =
            current.concat(values);
          break;
        }
        case "EQ": {
          const current: unknown[] = Array.isArray(target.values)
            ? (target.values as unknown[])
            : [];
          target.values = current.concat(values);
          break;
        }
        case "NEQ": {
          const current: unknown[] = Array.isArray(
            (target as Record<string, unknown>).neqValues,
          )
            ? ((target as Record<string, unknown>).neqValues as unknown[])
            : [];
          (target as Record<string, unknown>).neqValues =
            current.concat(values);
          break;
        }
        default: {
          // Fallback: prefer text-like filter when a single value is present
          if (values.length > 0) target.value = values[0];
        }
      }
    });
  }

  return clientFilters;
}

// ---------------------------------------------------------------------------
// Convenience helpers for UI builders (non-React)
// ---------------------------------------------------------------------------

/**
 * Extracts the *top-level* StructuredFilters from a FilterGroup.
 * If the group contains nested groups, those are **not** traversed – the
 * function is intended for basic functionality where we only support a single AND level.
 */
export function filterGroupToList(group: FilterGroup): StructuredFilter[] {
  return group.filters.filter((f): f is StructuredFilter => !isFilterGroup(f));
}

/**
 * Converts a flat list of StructuredFilters into an AND-group.
 * This is useful for the FilterBuilder which manipulates the filters as a
 * simple array and then needs to emit a proper `FilterGroup` back to callers.
 */
export function listToFilterGroup(filters: StructuredFilter[]): FilterGroup {
  return { operator: "AND", filters: filters.slice() };
}

// ---------------------------------------------------------------------------
// Validation helpers used by FilterBuilder/Modal UIs
// ---------------------------------------------------------------------------

/**
 * Returns true if the given StructuredFilter has all required values and can
 * be considered "complete" for saving.
 *
 * Logic mirrors the UI validation rules:
 *   • IS_NULL / IS_NOT_NULL -> no values needed
 *   • BETWEEN              -> exactly two non-empty values required
 *   • All others           -> at least one non-empty value
 */
export function isFilterComplete(filter: StructuredFilter): boolean {
  // A filter with a null field or null operator is never complete.
  if (!filter.field || !filter.op) return false;

  // Operators that do not require a value array
  if (filter.op === "IS_NULL" || filter.op === "IS_NOT_NULL") return true;

  const vals = Array.isArray(filter.values) ? filter.values : [];

  if (filter.op === "BETWEEN") {
    return (
      vals.length === 2 &&
      vals[0] !== "" &&
      vals[0] !== undefined &&
      vals[1] !== "" &&
      vals[1] !== undefined
    );
  }

  // For other operators, if values can be null, we need to handle that.
  // Assuming null values means it's not complete unless the operator is IS_NULL or IS_NOT_NULL (handled above).
  if (filter.values === null || filter.values === undefined) return false;

  return (
    vals.length > 0 &&
    vals.every((v) => v !== "" && v !== undefined && v !== null)
  );
}

/**
 * Checks a top-level FilterGroup for any incomplete StructuredFilters.
 * Traverses nested groups recursively.
 */
export function hasIncompleteFilters(group: FilterGroup): boolean {
  return group.filters.some((f) =>
    isFilterGroup(f) ? hasIncompleteFilters(f) : !isFilterComplete(f),
  );
}

// Constant empty AND-group reused for defaults
export const EMPTY_GROUP: FilterGroup = { operator: "AND", filters: [] };

// Record filtering – apply a FilterGroup to a list of plain records
// ---------------------------------------------------------------------------

function isStructuredFilter(node: unknown): node is StructuredFilter {
  return (
    typeof node === "object" && node !== null && "field" in node && "op" in node
  );
}

function getFieldValue(
  record: Record<string, unknown>,
  fieldPath: string,
): [unknown, boolean] {
  if (!fieldPath.includes(".")) {
    const exists = Object.hasOwn(record, fieldPath);
    return [record[fieldPath], exists];
  }
  const parts = fieldPath.split(".");
  let cur: unknown = record;
  for (const part of parts) {
    if (cur == null || typeof cur !== "object") return [undefined, false];
    const obj = cur as Record<string, unknown>;
    if (!Object.hasOwn(obj, part)) {
      return [undefined, false];
    }
    cur = obj[part];
  }
  return [cur, true];
}

function toCamelCase(segment: string): string {
  return segment.replace(/_([a-z])/g, (_m, c: string) => c.toUpperCase());
}

function toSnakeCase(segment: string): string {
  return segment
    .replace(/([A-Z])/g, (m: string) => `_${m.toLowerCase()}`)
    .replace(/^_/, "");
}

function getFieldValueWithCaseFallback(
  record: Record<string, unknown>,
  fieldPath: string,
): [unknown, boolean] {
  // 1) Try as-is
  const direct = getFieldValue(record, fieldPath);
  if (direct[1]) return direct;

  // 2) Try camelCase path if underscores are present
  if (fieldPath.includes("_")) {
    const camelPath = fieldPath
      .split(".")
      .map((seg) => toCamelCase(seg))
      .join(".");
    const camel = getFieldValue(record, camelPath);
    if (camel[1]) return camel;
  }

  // 3) Try snake_case path if camelCase likely
  if (/[A-Z]/.test(fieldPath)) {
    const snakePath = fieldPath
      .split(".")
      .map((seg) => toSnakeCase(seg))
      .join(".");
    const snake = getFieldValue(record, snakePath);
    if (snake[1]) return snake;
  }

  return [undefined, false];
}

function toStringSafe(value: unknown): string {
  return value == null ? "" : String(value);
}

function asNumber(value: unknown): number | undefined {
  if (typeof value === "number") return value;
  const n = Number(value);
  return Number.isFinite(n) ? n : undefined;
}

function evaluatePredicate(
  record: Record<string, unknown>,
  filter: StructuredFilter,
): boolean {
  if (!filter.field || !filter.op) return true;

  const op = filter.op;
  const [recordValue, exists] = getFieldValueWithCaseFallback(
    record,
    filter.field,
  );
  const values = Array.isArray(filter.values)
    ? filter.values
    : filter.values == null
      ? []
      : [filter.values];

  switch (op) {
    case "IS_NULL": {
      return exists && (recordValue === null || recordValue === undefined);
    }
    case "IS_NOT_NULL": {
      return exists && recordValue !== null && recordValue !== undefined;
    }
    case "ILIKE": {
      if (values.length === 0) return true;
      const hay = toStringSafe(recordValue).toLowerCase();
      return values.some((v) => hay.includes(toStringSafe(v).toLowerCase()));
    }
    case "LIKE": {
      if (values.length === 0) return true;
      const hay = toStringSafe(recordValue);
      return values.some((v) => hay.includes(toStringSafe(v)));
    }
    case "IN": {
      return values.some((v) => recordValue === v);
    }
    case "NOT_IN": {
      return !values.some((v) => recordValue === v);
    }
    case "EQ": {
      // When multiple values are provided for EQ, treat as OR (similar to IN)
      return values.some((v) => recordValue === v);
    }
    case "NEQ": {
      // When multiple values are provided, ensure recordValue differs from all
      return values.every((v) => recordValue !== v);
    }
    case "GT":
    case "GTE":
    case "LT":
    case "LTE": {
      if (values.length === 0) return true;
      const a = asNumber(recordValue);
      const b = asNumber(values[0]);
      if (a === undefined || b === undefined) return true;
      if (op === "GT") return a > b;
      if (op === "GTE") return a >= b;
      if (op === "LT") return a < b;
      return a <= b; // LTE
    }
    case "BETWEEN": {
      if (values.length < 2) return true;
      const a = asNumber(recordValue);
      const lo = asNumber(values[0]);
      const hi = asNumber(values[1]);
      if (a === undefined || lo === undefined || hi === undefined) return true;
      return a >= lo && a <= hi;
    }
    default: {
      return true;
    }
  }
}

function evaluateGroup(
  record: Record<string, unknown>,
  group: FilterGroup,
): boolean {
  const op = group.operator;
  if (op === "AND") {
    return group.filters.every((node) =>
      isFilterGroup(node)
        ? evaluateGroup(record, node)
        : isStructuredFilter(node)
          ? evaluatePredicate(record, node)
          : true,
    );
  }
  // OR
  return group.filters.some((node) =>
    isFilterGroup(node)
      ? evaluateGroup(record, node)
      : isStructuredFilter(node)
        ? evaluatePredicate(record, node)
        : true,
  );
}

export function applyFilterGroupToRecords(
  records: Array<Record<string, unknown>>,
  filters: FilterGroup | Array<StructuredFilter> | undefined,
): Array<Record<string, unknown>> {
  if (!filters) return records.slice();
  const group: FilterGroup = Array.isArray(filters)
    ? { operator: "AND", filters }
    : filters;
  if (!Array.isArray(group.filters) || group.filters.length === 0) {
    return records.slice();
  }
  return records.filter((r) => evaluateGroup(r, group));
}

// ---------------------------------------------------------------------------
// Parsing helpers for wire params (typed, no any)
// ---------------------------------------------------------------------------

export function parseFiltersParam(
  filtersParam: unknown,
): FilterGroup | Array<StructuredFilter> | undefined {
  if (typeof filtersParam !== "string" || filtersParam.trim() === "") {
    return undefined;
  }
  try {
    const parsed = JSON.parse(filtersParam) as unknown;
    if (Array.isArray(parsed)) {
      // Assume array of StructuredFilter
      return parsed as Array<StructuredFilter>;
    }
    if (
      parsed &&
      typeof parsed === "object" &&
      "filters" in (parsed as Record<string, unknown>) &&
      Array.isArray((parsed as { filters: unknown }).filters)
    ) {
      return parsed as FilterGroup;
    }
  } catch {
    // ignore invalid JSON
  }
  return undefined;
}
